import { DateTime } from "luxon";
import { useMemo } from "react";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useGetHoursSummary } from "../pages/ManagementCenter/HoursSummary/hooks";
import { getOtWeekStart } from "../utils/dateUtils";
import type { DayItem, HoursSummaryEmployeeDayShift, NurseInShift } from "../utils/models";
import { isAgencyNurse } from "../utils/utilFunctions";

type WeekGroup = {
	otWeekStart: DateTime;
	otWeekEnd: DateTime;
	nurseShifts: Map<number, NurseInShift[]>;
};

// Module-scoped so the "empty" early-return preserves identity across renders.
const EMPTY_COSTLY_SHIFTS_BY_DAY: ReadonlyMap<string, NurseInShift[]> = new Map();
const EMPTY_SHIFT_HOURS_BY_ID: ReadonlyMap<string, HoursSummaryEmployeeDayShift> = new Map();
const EMPTY_ACTUAL_SHIFT_IDS: ReadonlySet<string> = new Set();
const EMPTY_RESULT = {
	costlyShiftsByDay: EMPTY_COSTLY_SHIFTS_BY_DAY as Map<string, NurseInShift[]>,
	shiftHoursById: EMPTY_SHIFT_HOURS_BY_ID as Map<string, HoursSummaryEmployeeDayShift>,
	actualShiftIds: EMPTY_ACTUAL_SHIFT_IDS as Set<string>,
};

/**
 * Future-looking "costly staff" projection per day:
 *   - All agency NurseInShift on the day, AND
 *   - The specific OT-causing shifts of non-agency staff who are projected into weekly OT
 *     within the OT-week (respecting department.week_start_day) the day belongs to.
 *
 * Hours come from the BE-canonical `wage_breakdown_rows`-driven hours summary
 * (useGetHoursSummary). Salaried filtering happens server-side.
 *
 * For shifts on days inside an OT-week that ended before today, an actuals lookup
 * by `(nurseId, date)` substitutes punched hours into `shiftHoursById` when the BE
 * has a corresponding `wage_breakdown_rows_actual` entry. The substituted shift IDs
 * are returned in `actualShiftIds` so the consumer can mark them "ACTUAL" in the UI.
 *
 * Selection rule for OT-causing shifts of a single nurse who has weekly OT:
 *  - Both recurring & single shifts: if recurring alone is already at/over the limit, all singles
 *    are costly; else walk singles chronologically and flag from the first that pushes the
 *    running total over the limit onward.
 *  - Only recurring (no single): sort recurring by startTime desc, take from latest until
 *    accumulated hours ≥ otHours.
 *  - Only single (no recurring): same as above, applied to singles.
 *
 * Returns a Map keyed by `dayItem.date.toISODate()`, the shiftHoursById map, and the
 * set of shift IDs whose hours came from punched data.
 */
type CostlyStaffDayInput = Pick<DayItem, "date" | "shifts">;

export function useFutureLookingCostlyStaff(
	dayItems: CostlyStaffDayInput[],
	enabled: boolean,
): {
	costlyShiftsByDay: Map<string, NurseInShift[]>;
	shiftHoursById: Map<string, HoursSummaryEmployeeDayShift>;
	actualShiftIds: Set<string>;
} {
	const { chosenDepartment } = useHospitalData();
	const weekStartDay = chosenDepartment.week_start_day;

	const { sinceDate, endDate, pastRange } = useMemo(() => {
		if (dayItems.length === 0) return { sinceDate: undefined, endDate: undefined, pastRange: undefined };
		const today = DateTime.now().startOf("day");
		let min = dayItems[0].date;
		let max = dayItems[0].date;
		let pastMin: DateTime | undefined;
		let pastMax: DateTime | undefined;
		for (const di of dayItems) {
			if (di.date < min) min = di.date;
			if (di.date > max) max = di.date;
			const otWeekEnd = getOtWeekStart(di.date, weekStartDay).plus({ days: 6 }).endOf("day");
			if (otWeekEnd < today) {
				if (!pastMin || di.date < pastMin) pastMin = di.date;
				if (!pastMax || di.date > pastMax) pastMax = di.date;
			}
		}
		return {
			sinceDate: min,
			endDate: max,
			pastRange: pastMin && pastMax ? { min: pastMin, max: pastMax } : undefined,
		};
	}, [dayItems, weekStartDay]);

	const hoursSummaryEnabled = enabled && !!sinceDate && !!endDate;
	const { data: hoursSummary } = useGetHoursSummary({
		sinceDate: sinceDate ?? DateTime.now(),
		endDate,
		isActual: false,
		includeEmployeeDetails: true,
		enabled: hoursSummaryEnabled,
	});
	const { data: hoursSummaryActual } = useGetHoursSummary({
		sinceDate: pastRange?.min ?? DateTime.now(),
		endDate: pastRange?.max,
		isActual: true,
		includeEmployeeDetails: true,
		enabled: enabled && !!pastRange,
	});

	return useMemo(() => {
		if (!enabled || dayItems.length === 0) return EMPTY_RESULT;

		const today = DateTime.now().startOf("day");

		// Index actuals by `${nurseId}|${date}` so we can substitute on a per-shift basis.
		const actualByNurseAndDate = new Map<string, HoursSummaryEmployeeDayShift>();
		for (const employee of hoursSummaryActual?.employeeDetails ?? []) {
			for (const s of employee.shifts) {
				actualByNurseAndDate.set(`${employee.nurseId}|${s.date}`, s);
			}
		}

		// OT detection runs on scheduled values (`beShiftsByNurse`) — that decides which nurses
		// crossed the threshold and which shifts are OT-causing. For past-OT-week shifts that
		// also have a punched actuals entry, `shiftHoursById` exposes the actual `totalHours`
		// (and we record `actualShiftIds` so the consumer can mark the row "ACTUAL"). This
		// keeps detection stable while making the displayed hours reflect punched reality.
		const shiftHoursById = new Map<string, HoursSummaryEmployeeDayShift>();
		const actualShiftIds = new Set<string>();
		const beShiftsByNurse = new Map<number, HoursSummaryEmployeeDayShift[]>();
		for (const employee of hoursSummary?.employeeDetails ?? []) {
			beShiftsByNurse.set(employee.nurseId, employee.shifts);
			for (const s of employee.shifts) {
				const otWeekEnd = getOtWeekStart(DateTime.fromISO(s.date), weekStartDay).plus({ days: 6 }).endOf("day");
				const actual = otWeekEnd < today ? actualByNurseAndDate.get(`${employee.nurseId}|${s.date}`) : undefined;
				shiftHoursById.set(s.shiftId, actual ?? s);
				if (actual) actualShiftIds.add(s.shiftId);
			}
		}

		// Group dayItems & FE shifts by OT-week. Use the shared `getOtWeekStart` helper, which
		// mirrors the BE's `get_week_start_for_day` so our boundaries match the
		// wage_breakdown_rows bucketing.
		const otWeeks = new Map<string, WeekGroup>();
		for (const dayItem of dayItems) {
			const otWeekStart = getOtWeekStart(dayItem.date, weekStartDay);
			const otWeekKey = otWeekStart.toISODate() ?? "";

			let group = otWeeks.get(otWeekKey);
			if (!group) {
				group = {
					otWeekStart,
					otWeekEnd: otWeekStart.plus({ days: 6 }).endOf("day"),
					nurseShifts: new Map(),
				};
				otWeeks.set(otWeekKey, group);
			}

			for (const shift of dayItem.shifts) {
				let arr = group.nurseShifts.get(shift.nurseId);
				if (!arr) {
					arr = [];
					group.nurseShifts.set(shift.nurseId, arr);
				}
				arr.push(shift);
			}
		}

		// Costly set, keyed by NurseInShift.id so we don't double-bucket between agency + OT branches.
		const costlyShiftIds = new Set<string>();

		// 1) Agency costly — every agency NurseInShift on every visible day.
		for (const dayItem of dayItems) {
			for (const shift of dayItem.shifts) {
				if (isAgencyNurse(shift.type)) {
					costlyShiftIds.add(shift.id);
				}
			}
		}

		// 2) OT costly — per nurse per OT-week, using BE hours (mixed actual/scheduled).
		const hoursOf = (s: NurseInShift): number => shiftHoursById.get(s.id)?.totalHours ?? 0;

		for (const group of otWeeks.values()) {
			for (const [nurseId, shifts] of group.nurseShifts) {
				if (shifts.length === 0) continue;
				if (isAgencyNurse(shifts[0].type)) continue;

				// Sum BE hours for this nurse across days that fall inside this OT-week.
				const beShifts = beShiftsByNurse.get(nurseId) ?? [];
				let weeklyOtHours = 0;
				let totalHours = 0;
				for (const beShift of beShifts) {
					const beDate = DateTime.fromISO(beShift.date);
					if (beDate < group.otWeekStart || beDate > group.otWeekEnd) continue;
					weeklyOtHours += beShift.weeklyOtHours;
					totalHours += beShift.totalHours;
				}
				if (weeklyOtHours <= 0) continue;

				const hoursLimit = totalHours - weeklyOtHours;

				const recurring = shifts.filter((s) => s.recurringShiftId != null);
				const single = shifts.filter((s) => s.recurringShiftId == null);

				if (recurring.length > 0 && single.length > 0) {
					const recurringHours = recurring.reduce((sum, s) => sum + hoursOf(s), 0);
					if (recurringHours >= hoursLimit) {
						for (const s of single) costlyShiftIds.add(s.id);
					} else {
						const sorted = [...single].sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis());
						let running = recurringHours;
						let crossed = false;
						for (const s of sorted) {
							running += hoursOf(s);
							if (!crossed && running > hoursLimit) crossed = true;
							if (crossed) costlyShiftIds.add(s.id);
						}
					}
				} else if (recurring.length > 0) {
					const sorted = [...recurring].sort((a, b) => b.startTime.toMillis() - a.startTime.toMillis());
					let acc = 0;
					for (const s of sorted) {
						costlyShiftIds.add(s.id);
						acc += hoursOf(s);
						if (acc >= weeklyOtHours) break;
					}
				} else if (single.length > 0) {
					const sorted = [...single].sort((a, b) => b.startTime.toMillis() - a.startTime.toMillis());
					let acc = 0;
					for (const s of sorted) {
						costlyShiftIds.add(s.id);
						acc += hoursOf(s);
						if (acc >= weeklyOtHours) break;
					}
				}
			}
		}

		// Bucket costly NurseInShifts by day.
		const byDay = new Map<string, NurseInShift[]>();
		for (const dayItem of dayItems) {
			const key = dayItem.date.toISODate() ?? "";
			const dayCostly: NurseInShift[] = [];
			for (const shift of dayItem.shifts) {
				if (costlyShiftIds.has(shift.id)) dayCostly.push(shift);
			}
			if (dayCostly.length > 0) byDay.set(key, dayCostly);
		}

		return { costlyShiftsByDay: byDay, shiftHoursById, actualShiftIds };
	}, [enabled, dayItems, weekStartDay, hoursSummary, hoursSummaryActual]);
}
